/**
 * TemplatesTab — R151 · Feb 2026
 *
 * Custom Email Template library for the Comms Center. Workspace admins
 * (or authors) can create, edit, archive and preview named templates
 * with Handlebars-style tokens like {{client.name}}, {{project.name}},
 * {{user.name}}, {{sender.name}}. PMs pick from this library instead
 * of hand-authoring HTML for every send.
 *
 * Backend: routes_email_templates.py
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import SmartRichText from "@/components/SmartRichText";
import { Search, Plus, Pencil, Trash2, Archive, ArchiveRestore, Eye, Copy, FileText, Tag as TagIcon, Send, BarChart3 } from "lucide-react";

const SAMPLE_DATA = {
  client: { name: "Acme Coffee Co.", email: "hello@acme.com", company: "Acme Coffee Co." },
  project: { name: "Q1 Launch Campaign", code: "ACME-Q1-25" },
  brand: { name: "Acme Roast House" },
  user: { name: "Jane Recipient", email: "jane@acme.com" },
  sender: { name: "You", email: "you@acquilix.com" },
  today: new Date().toLocaleDateString(),
};

export default function TemplatesTab() {
  const { activeWorkspaceId, user } = useAuth();
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [editing, setEditing] = useState(null);           // template or "new"

  const load = async () => {
    if (!activeWorkspaceId) return;
    setBusy(true);
    try {
      const { data } = await api.get(
        `/workspaces/${activeWorkspaceId}/email-templates`,
        { params: { include_archived: showArchived } });
      setRows(data?.rows || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load templates")); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeWorkspaceId, showArchived]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) => [
      r.name, r.description, r.category, r.subject, ...(r.tags || []),
    ].filter(Boolean).some((v) => String(v).toLowerCase().includes(s)));
  }, [rows, q]);

  const grouped = useMemo(() => {
    const g = new Map();
    for (const r of filtered) {
      const cat = r.category || "Uncategorized";
      if (!g.has(cat)) g.set(cat, []);
      g.get(cat).push(r);
    }
    return Array.from(g.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [filtered]);

  return (
    <div className="space-y-4" data-testid="comms-templates-tab">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, category, subject, tag…"
            className="pl-8 h-9 text-sm"
            data-testid="templates-search"
          />
        </div>
        <label className="inline-flex items-center gap-1.5 text-xs cursor-pointer">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
            data-testid="templates-show-archived"
          />
          Show archived
        </label>
        <div className="ml-auto text-xs text-muted-foreground">
          {rows.length} template{rows.length === 1 ? "" : "s"}
        </div>
        <Button
          className="rounded-none h-9"
          onClick={() => setEditing({ __new: true })}
          data-testid="templates-new-btn"
        >
          <Plus className="w-3.5 h-3.5 mr-1.5" /> New template
        </Button>
      </div>

      {busy && rows.length === 0 && (
        <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>
      )}
      {!busy && filtered.length === 0 && (
        <div className="border border-dashed border-border p-10 text-center" data-testid="templates-empty">
          <FileText className="w-8 h-8 text-muted-foreground/50 mx-auto mb-2" />
          <div className="text-sm font-semibold">
            {rows.length === 0 ? "No templates yet" : "No templates match this search"}
          </div>
          <p className="text-xs text-muted-foreground mt-1 max-w-md mx-auto">
            {rows.length === 0
              ? "Save your best-performing email as a template so every PM sends the same polished copy."
              : "Try a different search term or clear filters."}
          </p>
        </div>
      )}

      {grouped.map(([cat, items]) => (
        <div key={cat} className="border border-border" data-testid={`templates-group-${cat}`}>
          <div className="px-3 py-2 bg-muted/40 border-b border-border text-[10px] uppercase tracking-widest text-muted-foreground font-semibold flex items-center gap-1.5">
            <TagIcon className="w-3 h-3" /> {cat} · {items.length}
          </div>
          <ul className="divide-y divide-border">
            {items.map((t) => (
              <TemplateRow
                key={t.id}
                t={t}
                canEdit={user?.id === t.created_by || user?.is_super_admin}
                onEdit={() => setEditing(t)}
                onDeleted={load}
                onArchiveToggled={load}
              />
            ))}
          </ul>
        </div>
      ))}

      {editing && (
        <TemplateEditor
          template={editing.__new ? null : editing}
          wsId={activeWorkspaceId}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function TemplateRow({ t, canEdit, onEdit, onDeleted, onArchiveToggled }) {
  const [busy, setBusy] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  const [previewSendBusy, setPreviewSendBusy] = useState(false);
  const del = async () => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    setBusy(true);
    try {
      await api.delete(`/email-templates/${t.id}`);
      toast.success("Deleted");
      onDeleted();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
    finally { setBusy(false); }
  };
  const toggleArchive = async () => {
    setBusy(true);
    try {
      await api.patch(`/email-templates/${t.id}`, { archived: !t.archived });
      toast.success(t.archived ? "Restored" : "Archived");
      onArchiveToggled();
    } catch (e) { toast.error(apiErrorMessage(e, "Update failed")); }
    finally { setBusy(false); }
  };
  const duplicate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/workspaces/${t.workspace_id}/email-templates`, {
        name: `${t.name} (copy)`,
        description: t.description,
        category: t.category,
        subject: t.subject,
        body_html: t.body_html,
        body_text: t.body_text,
        variables: t.variables,
        tags: t.tags,
      });
      toast.success("Duplicated");
      onArchiveToggled();
      onEdit(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Duplicate failed")); }
    finally { setBusy(false); }
  };

  const previewSend = async () => {
    setPreviewSendBusy(true);
    try {
      const { data } = await api.post(`/email-templates/${t.id}/preview-send`, { data: SAMPLE_DATA });
      const status = data?.delivery?.overall || "sent";
      if (status === "sent") toast.success(`Preview sent to ${data.recipient}`);
      else if (status === "skipped") toast.warning(`Preview logged but not delivered (${data?.delivery?.email?.reason || "no provider"})`);
      else toast.error(`Preview send failed: ${data?.delivery?.reason || "unknown"}`);
    } catch (e) { toast.error(apiErrorMessage(e, "Preview send failed")); }
    finally { setPreviewSendBusy(false); }
  };

  return (
    <li className={`px-3 py-2.5 flex items-start gap-3 hover:bg-muted/20 ${t.archived ? "opacity-60" : ""}`} data-testid={`template-row-${t.id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold truncate">{t.name}</span>
          {t.archived && (
            <span className="text-[9px] uppercase tracking-widest bg-muted text-muted-foreground border border-border px-1.5 py-0.5">
              Archived
            </span>
          )}
          {(t.tags || []).map((tag) => (
            <span key={tag} className="text-[9px] uppercase tracking-widest bg-primary/10 text-primary border border-primary/30 px-1.5 py-0.5">
              {tag}
            </span>
          ))}
        </div>
        <div className="text-[11px] text-muted-foreground truncate mt-0.5">
          Subject · <span className="font-mono">{t.subject}</span>
        </div>
        {t.description && (
          <div className="text-[10px] text-muted-foreground mt-0.5">{t.description}</div>
        )}
        {(t.detected_tokens || []).length > 0 && (
          <div className="mt-1 flex items-center gap-1 flex-wrap">
            <span className="text-[9px] text-muted-foreground uppercase tracking-widest">Tokens:</span>
            {(t.detected_tokens || []).slice(0, 8).map((tok) => (
              <code key={tok} className="text-[10px] bg-muted px-1 border border-border" data-testid={`template-token-${t.id}-${tok}`}>
                {"{{"}
                {tok}
                {"}}"}
              </code>
            ))}
            {(t.detected_tokens || []).length > 8 && (
              <span className="text-[9px] text-muted-foreground">+{(t.detected_tokens || []).length - 8}</span>
            )}
          </div>
        )}
        {t.created_by_name && (
          <div className="text-[9px] text-muted-foreground mt-1">by {t.created_by_name}</div>
        )}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <button
          type="button"
          onClick={() => setPreviewOpen(true)}
          className="p-1.5 hover:bg-muted rounded-none border border-transparent hover:border-border"
          title="Preview"
          data-testid={`template-preview-${t.id}`}
        >
          <Eye className="w-3.5 h-3.5" />
        </button>
        <button
          type="button"
          onClick={previewSend}
          disabled={previewSendBusy}
          className="p-1.5 hover:bg-emerald-50 hover:text-emerald-700 rounded-none border border-transparent hover:border-emerald-300 disabled:opacity-50"
          title="Send preview to my email"
          data-testid={`template-preview-send-${t.id}`}
        >
          <Send className="w-3.5 h-3.5" />
        </button>
        <button
          type="button"
          onClick={() => setAnalyticsOpen(true)}
          className="p-1.5 hover:bg-muted rounded-none border border-transparent hover:border-border"
          title="Send analytics"
          data-testid={`template-analytics-${t.id}`}
        >
          <BarChart3 className="w-3.5 h-3.5" />
        </button>
        <button
          type="button"
          onClick={duplicate}
          disabled={busy}
          className="p-1.5 hover:bg-muted rounded-none border border-transparent hover:border-border disabled:opacity-50"
          title="Duplicate"
          data-testid={`template-duplicate-${t.id}`}
        >
          <Copy className="w-3.5 h-3.5" />
        </button>
        {canEdit && (
          <>
            <button
              type="button"
              onClick={onEdit}
              className="p-1.5 hover:bg-muted rounded-none border border-transparent hover:border-border"
              title="Edit"
              data-testid={`template-edit-${t.id}`}
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              onClick={toggleArchive}
              disabled={busy}
              className="p-1.5 hover:bg-muted rounded-none border border-transparent hover:border-border disabled:opacity-50"
              title={t.archived ? "Restore" : "Archive"}
              data-testid={`template-archive-${t.id}`}
            >
              {t.archived ? <ArchiveRestore className="w-3.5 h-3.5" /> : <Archive className="w-3.5 h-3.5" />}
            </button>
            <button
              type="button"
              onClick={del}
              disabled={busy}
              className="p-1.5 hover:bg-rose-50 hover:text-rose-700 rounded-none border border-transparent hover:border-rose-300 disabled:opacity-50"
              title="Delete"
              data-testid={`template-delete-${t.id}`}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </>
        )}
      </div>
      {previewOpen && (
        <TemplatePreviewDialog
          template={t}
          onClose={() => setPreviewOpen(false)}
        />
      )}
      {analyticsOpen && (
        <TemplateAnalyticsDialog
          template={t}
          onClose={() => setAnalyticsOpen(false)}
        />
      )}
    </li>
  );
}

function TemplateEditor({ template, wsId, onClose, onSaved }) {
  const isNew = !template;
  const [name, setName] = useState(template?.name || "");
  const [description, setDescription] = useState(template?.description || "");
  const [category, setCategory] = useState(template?.category || "");
  const [subject, setSubject] = useState(template?.subject || "");
  const [bodyHtml, setBodyHtml] = useState(template?.body_html || "");
  const [tagsRaw, setTagsRaw] = useState((template?.tags || []).join(", "));
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewSendBusy, setPreviewSendBusy] = useState(false);

  const previewSend = async () => {
    if (isNew) { toast.error("Save the template first, then send a preview"); return; }
    setPreviewSendBusy(true);
    try {
      const { data } = await api.post(`/email-templates/${template.id}/preview-send`, { data: SAMPLE_DATA });
      const status = data?.delivery?.overall || "sent";
      if (status === "sent") toast.success(`Preview sent to ${data.recipient}`);
      else if (status === "skipped") toast.warning(`Preview logged but not delivered (${data?.delivery?.email?.reason || "no email provider"})`);
      else toast.error(`Preview send failed: ${data?.delivery?.reason || "unknown"}`);
    } catch (e) { toast.error(apiErrorMessage(e, "Preview send failed")); }
    finally { setPreviewSendBusy(false); }
  };

  const save = async () => {
    if (!name.trim() || !subject.trim() || !bodyHtml.trim()) {
      toast.error("Name, subject, and body are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        description: description.trim() || null,
        category: category.trim() || null,
        subject: subject.trim(),
        body_html: bodyHtml,
        tags: tagsRaw.split(",").map((s) => s.trim()).filter(Boolean),
      };
      if (isNew) {
        await api.post(`/workspaces/${wsId}/email-templates`, payload);
        toast.success("Template created");
      } else {
        await api.patch(`/email-templates/${template.id}`, payload);
        toast.success("Template saved");
      }
      onSaved();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setSaving(false); }
  };

  const preview = async () => {
    if (!bodyHtml || !subject) { toast.error("Fill subject + body first"); return; }
    try {
      if (isNew) {
        // Client-side render for unsaved drafts
        const rx = /\{\{\s*([\w.-]+)\s*\}\}/g;
        const get = (path) => {
          let cur = SAMPLE_DATA;
          for (const p of path.split(".")) { if (cur && typeof cur === "object") cur = cur[p]; else return ""; }
          return cur == null ? "" : String(cur);
        };
        setPreviewData({
          subject: subject.replace(rx, (_, k) => get(k)),
          body_html: bodyHtml.replace(rx, (_, k) => get(k)),
        });
      } else {
        const { data } = await api.post(`/email-templates/${template.id}/render`, { data: SAMPLE_DATA });
        setPreviewData(data);
      }
      setShowPreview(true);
    } catch (e) { toast.error(apiErrorMessage(e, "Preview failed")); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="template-editor-dialog">
        <DialogHeader>
          <DialogTitle>{isNew ? "New email template" : `Edit — ${template.name}`}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Name *</Label>
              <Input
                value={name} onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Project kick-off — welcome email"
                className="h-9 text-sm mt-1"
                data-testid="template-editor-name"
              />
            </div>
            <div>
              <Label className="text-xs">Category</Label>
              <Input
                value={category} onChange={(e) => setCategory(e.target.value)}
                placeholder="e.g. project-updates, follow-ups"
                list="template-category-list"
                className="h-9 text-sm mt-1"
                data-testid="template-editor-category"
              />
              <datalist id="template-category-list">
                <option value="Project updates" />
                <option value="Follow-ups" />
                <option value="Invoices" />
                <option value="Onboarding" />
                <option value="Client communication" />
              </datalist>
            </div>
          </div>
          <div>
            <Label className="text-xs">Description</Label>
            <Input
              value={description} onChange={(e) => setDescription(e.target.value)}
              placeholder="When should someone reach for this template?"
              className="h-9 text-sm mt-1"
              data-testid="template-editor-description"
            />
          </div>
          <div>
            <Label className="text-xs">Subject *</Label>
            <Input
              value={subject} onChange={(e) => setSubject(e.target.value)}
              placeholder="{{project.name}} — kick-off · {{today}}"
              className="h-9 text-sm mt-1 font-mono"
              data-testid="template-editor-subject"
            />
          </div>
          <div>
            <Label className="text-xs">Body (rich text) *</Label>
            <div className="border border-border mt-1">
              <SmartRichText
                value={bodyHtml}
                onChange={setBodyHtml}
                placeholder="Hi {{client.name}}, welcome aboard for {{project.name}}…"
                testid="template-editor-body"
              />
            </div>
            <div className="text-[10px] text-muted-foreground mt-1">
              Use <code className="bg-muted px-1">{"{{client.name}}"}</code>, <code className="bg-muted px-1">{"{{project.name}}"}</code>, <code className="bg-muted px-1">{"{{user.name}}"}</code>, <code className="bg-muted px-1">{"{{sender.name}}"}</code>, <code className="bg-muted px-1">{"{{today}}"}</code>… anything you can nest a dot-path into. Missing values render blank.
            </div>
          </div>
          <div>
            <Label className="text-xs">Tags</Label>
            <Input
              value={tagsRaw} onChange={(e) => setTagsRaw(e.target.value)}
              placeholder="comma, separated, keywords"
              className="h-9 text-sm mt-1"
              data-testid="template-editor-tags"
            />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="ghost" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button variant="outline" className="rounded-none mr-auto" onClick={preview} data-testid="template-editor-preview">
            <Eye className="w-3.5 h-3.5 mr-1.5" /> Preview with sample data
          </Button>
          {!isNew && (
            <Button variant="outline" className="rounded-none" onClick={previewSend} disabled={previewSendBusy} data-testid="template-editor-preview-send">
              <Send className="w-3.5 h-3.5 mr-1.5" /> {previewSendBusy ? "Sending…" : "Send preview to me"}
            </Button>
          )}
          <Button className="rounded-none" onClick={save} disabled={saving} data-testid="template-editor-save">
            {saving ? "Saving…" : (isNew ? "Create template" : "Save changes")}
          </Button>
        </DialogFooter>
      </DialogContent>
      {showPreview && previewData && (
        <PreviewRenderedDialog data={previewData} onClose={() => setShowPreview(false)} />
      )}
    </Dialog>
  );
}

function PreviewRenderedDialog({ data, onClose }) {
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="template-preview-dialog">
        <DialogHeader>
          <DialogTitle>Preview · with sample data</DialogTitle>
        </DialogHeader>
        <div className="border border-border p-3 space-y-2">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Subject</div>
            <div className="text-sm font-semibold" data-testid="template-preview-subject">{data.subject}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Body</div>
            {/* eslint-disable-next-line react/no-danger */}
            <div
              className="prose prose-sm max-w-none mt-2 border-t border-border pt-2"
              dangerouslySetInnerHTML={{ __html: data.body_html }}
              data-testid="template-preview-body"
            />
          </div>
          {(data.tokens_missing || []).length > 0 && (
            <div className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 p-2 mt-2">
              Missing tokens (rendered blank): {data.tokens_missing.join(", ")}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="rounded-none">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TemplatePreviewDialog({ template, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.post(`/email-templates/${template.id}/render`, { data: SAMPLE_DATA });
        setData(data);
      } catch (e) { toast.error(apiErrorMessage(e, "Preview failed")); onClose(); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template.id]);
  if (!data) return null;
  return <PreviewRenderedDialog data={data} onClose={onClose} />;
}


// R156 · Feb 2026 — Analytics dialog: totals, trend, top senders, recent events.
function TemplateAnalyticsDialog({ template, onClose }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  useEffect(() => {
    setBusy(true); setErr(null);
    api.get(`/email-templates/${template.id}/analytics`)
      .then(({ data }) => setData(data))
      .catch((e) => setErr(apiErrorMessage(e, "Failed to load analytics")))
      .finally(() => setBusy(false));
  }, [template.id]);

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="template-analytics-dialog">
        <DialogHeader>
          <DialogTitle>Analytics — {template.name}</DialogTitle>
        </DialogHeader>
        {busy && <div className="p-6 text-sm text-muted-foreground">Loading…</div>}
        {err && <div className="p-4 text-sm text-rose-600">{err}</div>}
        {data && (
          <div className="space-y-4">
            {/* Totals */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatCard label="Sends" value={data.totals?.sends || 0} />
              <StatCard label="Previews" value={data.totals?.previews || 0} />
              <StatCard label="Unique recipients" value={data.totals?.unique_recipients || 0} />
              <StatCard label="Last sent" value={data.totals?.last_sent_at ? new Date(data.totals.last_sent_at).toLocaleDateString() : "—"} small />
            </div>

            {/* R268uu · Feb 2026 — Delivery breakdowns */}
            {(data.breakdown?.channel?.length > 0 || data.breakdown?.from_mode?.length > 0 || data.breakdown?.delivery?.length > 0) && (
              <div className="border p-3" data-testid="template-analytics-breakdown">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Delivery breakdown</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                  {data.breakdown?.channel?.length > 0 && (
                    <div>
                      <div className="text-[9px] uppercase tracking-widest text-muted-foreground mb-1">By channel</div>
                      <ul className="space-y-0.5">
                        {data.breakdown.channel.map((b) => (
                          <li key={b.key} className="flex items-center justify-between">
                            <span className="capitalize">{b.key}</span>
                            <span className="font-mono">{b.count}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {data.breakdown?.from_mode?.length > 0 && (
                    <div>
                      <div className="text-[9px] uppercase tracking-widest text-muted-foreground mb-1">Sender identity</div>
                      <ul className="space-y-0.5">
                        {data.breakdown.from_mode.map((b) => (
                          <li key={b.key} className="flex items-center justify-between">
                            <span>{b.key === "system" ? "System (workspace default)" : b.key === "user_mailbox" ? "Personal mailbox" : b.key}</span>
                            <span className="font-mono">{b.count}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {data.breakdown?.delivery?.length > 0 && (
                    <div>
                      <div className="text-[9px] uppercase tracking-widest text-muted-foreground mb-1">Delivery status</div>
                      <ul className="space-y-0.5">
                        {data.breakdown.delivery.map((b) => {
                          const tone = b.key === "sent" ? "text-emerald-700" : b.key === "failed" ? "text-rose-700" : "text-amber-700";
                          return (
                            <li key={b.key} className="flex items-center justify-between">
                              <span className={`capitalize ${tone}`}>{b.key}</span>
                              <span className="font-mono">{b.count}</span>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* R268uu · Feb 2026 — Failure diagnostics */}
            {data.failures?.length > 0 && (
              <div className="border border-rose-300 dark:border-rose-800 p-3" data-testid="template-analytics-failures">
                <div className="text-[10px] uppercase tracking-widest text-rose-700 dark:text-rose-400 mb-2 font-semibold flex items-center gap-2">
                  Failures &amp; troubleshooting
                  <span className="text-[9px] normal-case tracking-normal text-muted-foreground">({data.failures.length} recent)</span>
                </div>
                <ul className="space-y-2">
                  {data.failures.map((f) => (
                    <li key={f.id} className="text-xs bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-800 p-2"
                        data-testid={`template-analytics-failure-${f.id}`}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 border text-rose-700 border-rose-300 bg-rose-50">
                          {f.channel || "email"} · {f.from_mode === "user_mailbox" ? "personal" : "system"}
                        </span>
                        <span className="text-muted-foreground">{new Date(f.created_at).toLocaleString()}</span>
                        <span className="font-mono truncate">{f.recipient || "—"}</span>
                        {f.from_email && <span className="text-[10px] text-muted-foreground truncate">from: {f.from_email}</span>}
                      </div>
                      {f.reason && (
                        <div className="mt-1 font-mono text-[10px] text-rose-800 dark:text-rose-300 break-all">
                          reason: {f.reason}
                          {f.detail ? ` · ${typeof f.detail === "string" ? f.detail : JSON.stringify(f.detail)}` : ""}
                        </div>
                      )}
                      {f.hint && (
                        <div className="mt-1 text-[11px] text-foreground leading-snug">
                          <span className="text-[9px] uppercase tracking-widest text-muted-foreground mr-1">Fix:</span>
                          {f.hint}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Top senders */}
            {data.top_senders?.length > 0 && (
              <div className="border p-3">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Top senders</div>
                <ul className="space-y-1">
                  {data.top_senders.map((s) => (
                    <li key={s.user_id} className="flex items-center justify-between text-sm">
                      <span>{s.user_name || s.user_id}</span>
                      <span className="font-mono text-xs">{s.count}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Trend */}
            {data.trend?.length > 0 && (
              <div className="border p-3">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Daily activity (last 30 days)</div>
                <table className="w-full text-xs">
                  <thead className="text-[10px] uppercase text-muted-foreground">
                    <tr><th className="text-left py-1">Date</th><th className="text-right py-1">Sent</th><th className="text-right py-1">Previews</th></tr>
                  </thead>
                  <tbody>
                    {data.trend.slice().reverse().map((r) => (
                      <tr key={r.date} className="border-t">
                        <td className="py-1">{r.date}</td>
                        <td className="text-right font-mono">{r.sent || 0}</td>
                        <td className="text-right font-mono text-muted-foreground">{r.preview || 0}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Recent events */}
            <div className="border p-3">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Recent activity ({data.recent?.length || 0})</div>
              {(!data.recent || data.recent.length === 0) ? (
                <div className="text-xs text-muted-foreground italic py-2">No sends or previews recorded yet. Hit the Send-preview button on this template to see your first event here.</div>
              ) : (
                <ul className="divide-y">
                  {data.recent.map((e) => (
                    <li key={e.id} className="py-1.5 text-xs flex items-center gap-2" data-testid={`template-analytics-evt-${e.id}`}>
                      <span className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 border ${e.event === "sent" ? "text-emerald-700 border-emerald-300 bg-emerald-50" : "text-amber-700 border-amber-300 bg-amber-50"}`}>
                        {e.event === "sent" ? "sent" : "preview"}
                      </span>
                      <span className="text-muted-foreground">{new Date(e.created_at).toLocaleString()}</span>
                      <span className="font-mono truncate flex-1">{e.recipient || "—"}</span>
                      <span className="text-muted-foreground">{e.user_name || ""}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} className="rounded-none">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StatCard({ label, value, small }) {
  return (
    <div className="border p-3">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`font-mono mt-1 ${small ? "text-sm" : "text-2xl"}`}>{value}</div>
    </div>
  );
}
